import { lyricsTextOf, sectionLines, storedSections } from '../../../../services/songSectionsModel';
import { trackKey } from '../../../../services/studentTracksModel';

/**
 * Section markers on a track (2026-09-28). Anthony: "Add sections to the
 * track that line up with the labeled sections in the lyrics to make it
 * easier to jump from section to section when teaching", and, asked how the
 * times should be found: automatically from the timed lyrics, with a teacher
 * able to nudge a marker. The row that shows them is SectionJumpRow.js (not
 * "SectionMarkers.js": macOS file names ignore case, and that name overwrote
 * this file once).
 *
 * TWO DATA SETS THAT NOTHING JOINED UNTIL NOW:
 *   repertoire.sections  [{ id, label, startLine }], startLine indexing the
 *                        SONG's lyrics text (LyricsService)
 *   file.lyrics.timed    [{ t, text }], the TRACK's own lines, from lrclib or
 *                        Whisper, in seconds from the start of that file
 * They are different texts of the same words, so the join is by the words:
 * each section's first lyric line is matched to the timed lines IN ORDER, so
 * a chorus sung three times finds its first, second and third timing in turn.
 * A section whose words cannot be found gets no marker rather than a guess
 * (a marker that jumps to the wrong verse is worse than none).
 *
 * A teacher's nudge is stored on the song (`repertoire.sectionMarks`), keyed
 * by the track's TIMELINE (markKeyFor), and always wins over the automatic
 * time.
 */

const norm = (s) =>
	String(s || '')
		.toLowerCase()
		.replace(/[’']/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

/** Share of the longer line's words found in the other: 0..1. */
export const lineSimilarity = (a, b) => {
	const A = norm(a).split(' ').filter(Boolean);
	const B = norm(b).split(' ').filter(Boolean);
	if (!A.length || !B.length) return 0;
	const pool = [...B];
	let hit = 0;
	for (const w of A) {
		const i = pool.indexOf(w);
		if (i >= 0) {
			hit += 1;
			pool.splice(i, 1);
		}
	}
	return hit / Math.max(A.length, B.length);
};

const MATCH_AT = 0.6;
// How many of a section's opening lines to try when its first line is not
// found (a timed source that dropped or merged a line).
const TRY_LINES = 3;

/**
 * Automatic markers: [{ sectionId, label, t }] in song order, one per section
 * whose words were found in `timed`.
 */
export const autoSectionMarkers = ({ sections = [], lyricsText = '', timed = [] }) => {
	if (!Array.isArray(sections) || !sections.length || !Array.isArray(timed) || !timed.length || !lyricsText) return [];
	const sorted = [...sections].sort((a, b) => a.startLine - b.startLine);
	const out = [];
	let cursor = 0;
	for (const sec of sorted) {
		const lines = sectionLines(lyricsText, sorted, sec.id).slice(0, TRY_LINES);
		let found = null;
		for (let k = 0; k < lines.length && found === null; k += 1) {
			for (let j = cursor; j < timed.length; j += 1) {
				if (lineSimilarity(lines[k].text, timed[j].text) >= MATCH_AT) {
					// Matched the section's k-th line. Its earlier lines are MISSING
					// from the track (that is why they did not match), so the marker
					// goes on the line that was found: at worst one line late. Backing
					// up k lines lands on the previous section's last line instead.
					found = { index: j, next: j + 1 };
					break;
				}
			}
		}
		if (found) {
			out.push({ sectionId: sec.id, label: sec.label, t: Number(timed[found.index].t) || 0 });
			cursor = found.next;
		}
	}
	return out;
};

/** VERBATIM copy of functions/trackJobs.js timelineKeyOf; both suites pin
 *  'job:j1' -> 'mtol2o3'. Hashed, because a Storage path is not a safe
 *  Firestore field name or document id. */
export const hashBasis = (basis) => {
	let h = 5381;
	const b = String(basis || '');
	for (let i = 0; i < b.length; i += 1) h = ((h * 33) ^ b.charCodeAt(i)) >>> 0;
	return `m${h.toString(36)}`;
};

const ownBasis = (file) => (file.jobId ? `job:${file.jobId}` : `file:${trackKey(file)}`);
const byPath = (files, p) => (Array.isArray(files) ? files : []).find((f) => f && f.storagePath === p) || null;

/**
 * Which timeline a file is on, for storing a nudge or a trim. A lesson job's
 * Reference, Track and Vocals share one; a saved key shares its ORIGINAL's
 * (the tempo never changes), found through `keyOf` in `files`. Fixed
 * 2026-09-29: this used to hash `key:<keyOf>` for a saved key, which is not
 * the original's own key, so a marker nudged on the original never reached
 * its saved key. Without the original in `files`, the old basis is kept.
 * A CUT is its own timeline (a different clock), unlike for the musical key.
 */
export const markKeyFor = (file, files = []) => {
	if (!file) return null;
	if (file.keyOf) {
		const original = byPath(files, file.keyOf);
		return hashBasis(original ? ownBasis(original) : `key:${file.keyOf}`);
	}
	return hashBasis(ownBasis(file));
};

/**
 * The musical key's document (trackKeys/{id}) for a file, and how far this
 * file sits from it in semitones: a saved key and a cut share the ORIGINAL
 * recording's key (a cut of a saved key carries the shift through).
 * Returns { id, semitones }.
 */
export const keyDocFor = (file, files = []) => {
	let f = file;
	let semitones = 0;
	for (let hop = 0; f && hop < 4; hop += 1) {
		if (f.cutOf) {
			const src = byPath(files, f.cutOf);
			if (!src) break;
			f = src;
		} else if (f.keyOf) {
			semitones += Number(f.semitones) || 0;
			const src = byPath(files, f.keyOf);
			if (!src) return { id: hashBasis(`key:${f.keyOf}`), semitones };
			f = src;
		} else break;
	}
	return f ? { id: hashBasis(ownBasis(f)), semitones } : { id: null, semitones: 0 };
};

/**
 * The markers a teacher sees: automatic times with the song's stored nudges
 * applied. A section with a nudge but no automatic match still gets a marker
 * (the teacher placed it by ear). Sorted by time; `nudged` marks the hand-set.
 */
export const sectionMarkersFor = ({ song, file, timed, files = [] }) => {
	// Only a STORED set that still matches the song's lyrics: the generic
	// chips (a song with no lyrics) have no lines to find, and a set built
	// from other lyrics points at the wrong lines.
	const sections = (song && storedSections(song)) || [];
	if (!sections.length || !file) return [];
	const lyricsText = lyricsTextOf(song.lyrics);
	const auto = autoSectionMarkers({ sections, lyricsText, timed: timed || [] });
	const marks = (song.sectionMarks && song.sectionMarks[markKeyFor(file, files)]) || {};
	const byId = new Map(auto.map((m) => [m.sectionId, { ...m, nudged: false }]));
	for (const sec of sections) {
		const t = marks[sec.id];
		if (typeof t === 'number' && Number.isFinite(t) && t >= 0) byId.set(sec.id, { sectionId: sec.id, label: sec.label, t, nudged: true });
	}
	return [...byId.values()].sort((a, b) => a.t - b.t);
};

/** The marker the playhead is in (the last one at or before `t`), or null. */
export const currentMarker = (markers = [], t = 0) => {
	let cur = null;
	for (const m of markers) {
		if (m.t <= t + 0.05) cur = m;
		else break;
	}
	return cur;
};
